export interface BuildInfo {
  /** Abbreviated commit hash for the deployed artifact. */
  commit: string;
  /** Whether uncommitted changes were present when the artifact was built. */
  dirty: boolean;
  /** ISO 8601 timestamp when the artifact metadata was generated. */
  builtAt: string;
}

declare global {
  // eslint-disable-next-line no-var
  var __GIT_INFO__: BuildInfo | undefined;
}

export function isBuildInfo(value: unknown): value is BuildInfo {
  if (typeof value !== 'object' || value === null) return false;

  const candidate = value as BuildInfo;
  return (
    typeof candidate.commit === 'string' &&
    typeof candidate.dirty === 'boolean' &&
    typeof candidate.builtAt === 'string'
  );
}

export function parseBuildInfo(value: unknown): BuildInfo | undefined {
  if (!isBuildInfo(value)) return undefined;

  return {
    commit: value.commit,
    dirty: value.dirty,
    builtAt: value.builtAt,
  };
}

export async function fetchGitInfo(
  uri = '/git-info.json'
): Promise<BuildInfo | undefined> {
  try {
    const response = await fetch(uri);
    if (!response.ok) return undefined;

    const json: unknown = await response.json();
    return parseBuildInfo(json);
  } catch {
    return undefined;
  }
}

export function exposeGitInfo(info: BuildInfo): void {
  globalThis.__GIT_INFO__ = info;
}

export function getGitInfo(): BuildInfo | undefined {
  return globalThis.__GIT_INFO__;
}

export async function loadGitInfo(
  uri = '/git-info.json'
): Promise<BuildInfo | undefined> {
  const info = await fetchGitInfo(uri);
  if (info) exposeGitInfo(info);
  return info;
}
